import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const WORK_PLAN_SERVER_NAME = "work_plan" as const;
export const WORK_PLAN_SUBMIT_TOOL_NAME = "submit_work_plan";

export const WORK_PLAN_TOOLS_METADATA = [
  {
    name: WORK_PLAN_SUBMIT_TOOL_NAME,
    description:
      "Propose a whole plan of work for other agents to run, with dependencies between the pieces. Use this when a request needs more than one specialist, or needs one piece finished before another can start. You propose; the plan is checked and scheduled by the system, which also chooses each agent. For a single piece of work with one specialist, delegate instead.",
    schema: {
      tasks: z
        .array(
          z.object({
            key: z
              .string()
              .describe(
                "A short name for this task, unique in the plan, used only to express dependencies."
              ),
            objective: z
              .string()
              .describe(
                "What this task has to achieve, in one or two sentences."
              ),
            capabilities: z
              .string()
              .describe(
                "What the work needs to reach or do — 'search the web for pricing pages', 'read our Notion specs'. Describe the capability, not the agent: which agent gets it is decided from this."
              ),
            dependsOn: z
              .array(z.string())
              .describe(
                "Keys of tasks that must succeed before this one starts. Empty means it starts immediately."
              ),
            reviewer: z
              .string()
              .nullable()
              .describe(
                "What a reviewer of this task should check, or null for no review. A review runs as its own task after this one, and never on the agent that did the work."
              ),
          })
        )
        .describe(
          "The tasks, in any order. At most four may start at once and the longest chain may be three."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Planning the work",
      done: "Work planned",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const WORK_PLAN_SERVER = {
  serverInfo: {
    name: WORK_PLAN_SERVER_NAME,
    version: "1.0.0",
    description:
      "Run several agents on one request: a plan of tasks with dependencies, validated and scheduled by the system, each task given to an agent that can actually reach what it needs.",
    authorization: null,
    icon: "ActionListCheckIcon",
    documentationUrl: null,
  },
  tools: WORK_PLAN_TOOLS_METADATA,
} as const satisfies ServerMetadata;
